import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useNavigate, useParams } from 'react-router-dom';
import { sanitizeInput } from '../../utils/sanitize';
import { getRandomHeadlineImage, getHeadlineImagePath } from '../../utils/headlineImages';
import SEO from '../../utils/SEO';
import GoogleAdSquare from '../googleads/GoogleAdSquare';


interface RoomCheckResult {
  exists: boolean;
  joinable: boolean;
  reason?: string;
  error?: string;
}

export default function DirectJoin() {
  const { roomId } = useParams<{ roomId: string }>();
  const [playerName, setPlayerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [randomImage, setRandomImage] = useState<string>("");
  const [roomCheck, setRoomCheck] = useState<RoomCheckResult | null>(null);
  const [isCheckingRoom, setIsCheckingRoom] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setRandomImage(getRandomHeadlineImage());
    
    // Check if room exists and is joinable
    const checkRoom = async () => {
      setIsCheckingRoom(true);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await fetch(`/api/rooms/${roomId}/check`, {
          signal: controller.signal
        }).finally(() => clearTimeout(timeoutId));
        
        if (!response.ok) {
          let errorMessage = 'Failed to check room status';
          try {
            const errorText = await response.text();
            if (errorText) {
              try {
                const errorJson = JSON.parse(errorText);
                if (errorJson.error) {
                  errorMessage = errorJson.error;
                }
              } catch (e) {
                // Ignore parse error
              }
            }
          } catch (e) {
            console.error("Failed to parse error response", e);
          }
          
          throw new Error(errorMessage);
        }
        
        const data = await response.json();
        console.log(`Room check data:`, data);
        
        setRoomCheck(data);
      } catch (err) {
        // Handle specific error types
        let errorMessage = 'Failed to check room status';
        
        if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
          errorMessage = 'Cannot connect to server. Please check your internet connection.';
        } else if (err instanceof DOMException && err.name === 'AbortError') {
          errorMessage = 'Request timed out. Please try again later.';
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        
        setRoomCheck({
          exists: false,
          joinable: false,
          error: errorMessage
        });
      } finally {
        setIsCheckingRoom(false);
      }
    };
    
    checkRoom();
  }, [roomId]);

  const handleJoinRoom = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const sanitizedName = sanitizeInput(playerName);
    if (!sanitizedName) {
      setError('Please enter your name');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`/api/rooms/${roomId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          playerName: sanitizedName,
          iconId: 0 // Initialize with default icon
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to join room');
      }

      const joinData = await response.json(); 
      navigate(`/app/room/${roomId}`, {
        state: { 
          isAdmin: false, 
          playerName: joinData.playerName, // Use unique name from backend
          playerId: joinData.playerId,
          iconId: 0 
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Don't sanitize on every keystroke - just set the raw value
    // Sanitization will happen when the form is submitted
    setPlayerName(e.target.value.slice(0, 25)); // Just limit length while typing
  };

  const handleBack = () => {
    window.location.replace('/');
  };

  // Display loading state while checking room
  if (isCheckingRoom) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-t-2 border-red-500 border-solid rounded-full animate-spin mx-auto mb-4"></div>
          <p>Checking room status...</p>
        </div>
      </div>
    );
  }
  
  // Display error if room doesn't exist or isn't joinable
  if (!roomCheck?.exists || !roomCheck?.joinable) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <svg className="w-full h-full text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold mb-4">
            {!roomCheck?.exists 
              ? "Room Not Found" 
              : "Cannot Join Room"}
          </h1>
          
          <p className="text-gray-400 mb-6">
            {roomCheck?.reason || roomCheck?.error || "This game room is no longer available."}
          </p>
          
          <div className="flex flex-col gap-4">
            <Button
              className="w-full bg-red-500 hover:bg-red-600"
              onClick={() => navigate('/app/join')}
            >
              Try Another Room
            </Button>
            
            <Button
              className="w-full border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              variant="outline"
              onClick={() => navigate('/app')}
            >
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <SEO 
        title="Join Spyfall Game"
        description="Join a Spyfall game room. Enter your name to start playing with friends in this exciting social deduction game."
        keywords="join spyfall game, room invitation, multiplayer game, social deduction game"
        noIndex={true}
      />
      {/* Google AdSense Ad */}
      <GoogleAdSquare />
      <div className="max-w-md mx-auto px-4 py-16">
        {/* Image Card */}
        <div className="relative w-64 h-64 bg-gradient-to-br from-red-900 to-red-700 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center border-2 border-red-500 overflow-hidden mb-8 mx-auto">
          <img
            src={getHeadlineImagePath(randomImage)}
            alt="Join Room"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-[3px] bg-gray-900/20 rounded-md flex flex-col items-center justify-center p-4">
            <div className="text-red-500 font-bold text-sm mb-2"></div>
            <div className="text-xl font-bold"></div>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Join Game Room</h1>
          <p className="text-gray-400">You've been invited to play Spyfall!</p>
          <p className="text-gray-400 text-md mt-2 font-bold">Room Code: <span className="text-red-500 font-bold text-lg">{roomId}</span></p>
        </div>

        <form onSubmit={handleJoinRoom} className="space-y-6">
          <div>
            <label htmlFor="playerName" className="block text-sm font-medium text-gray-300 mb-2">
              Your Name
            </label>
            <Input
              id="playerName"
              type="text"
              value={playerName}
              onChange={handleNameChange}
              className="w-full bg-gray-800 border-gray-700 text-white"
              placeholder="Enter your name"
              required
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="submit"
              className="flex-1 bg-red-500 hover:bg-red-600"
              disabled={!playerName.trim() || isLoading}
            >
              {isLoading ? 'Joining...' : 'Join Game'}
            </Button>
            <Button
              type="button"
              className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              variant="outline"
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </Button>
          </div>

          {/* Google AdSense Ad (Bottom) */}
          <div className="mt-6">
            <GoogleAdSquare />
          </div>
        </form>

      </div>
    </div>
  );
} 